import { useLayoutEffect, useState, type RefObject } from 'react'

export type IndicatorPosition = { left: number; width: number }

/**
 * Меряет активный таб, чтобы под него встала полоса-индикатор.
 *
 * ⚠️ ЗВАТЬ ЭТОТ ХУК ОБЯЗАН ТОТ, КТО ВЛАДЕЕТ `containerRef` — то есть компонент, который сам
 * рендерит контейнер. Не компонент внутри контейнера.
 *
 * Причина — порядок коммита React. Ref'ы прикрепляются и layout-эффекты запускаются обходом
 * снизу вверх: эффекты ДЕТЕЙ выполняются РАНЬШЕ, чем React прикрепит ref к их родителю.
 * Живи хук внутри самого индикатора (а он — потомок контейнера), в момент замера
 * `containerRef.current` был бы `null`: эффект молча вышел бы и больше не перезапустился, потому
 * что зависимости не меняются. Полоса не появлялась бы вовсе, пока не кликнешь по табу — тогда
 * меняется активный индекс, эффект прогоняется заново, и ref к тому времени уже на месте.
 *
 * Ни линт, ни тайпчек, ни тест такого не увидели бы: типы честные, ошибок нет, а ветка
 * `if (!container) return` сработала бы ровно один раз — в единственный момент, который важен.
 *
 * Возвращает `null`, когда активного таба нет (Студия — её открывают шестерёнкой, своего таба
 * у неё нет): полосы в этом случае быть не должно.
 */
export function useNavIndicator(
  containerRef: RefObject<HTMLElement | null>,
  activeIndex: number,
): IndicatorPosition | null {
  const [position, setPosition] = useState<IndicatorPosition | null>(null)

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return

    function measure() {
      const box = containerRef.current
      const active = box?.querySelector<HTMLElement>(`[data-tab="${activeIndex}"]`)

      if (!active) {
        setPosition(null)
        return
      }

      setPosition({ left: active.offsetLeft, width: active.offsetWidth })
    }

    measure()

    // Ширина таба «Задачи» меняется, когда с сервера приезжает бейдж со счётчиком, а ширины
    // всех табов — когда догружается шрифт. И то и другое случается ПОСЛЕ первой отрисовки,
    // так что одного замера мало: без наблюдателя полоса осталась бы под старой шириной.
    // Макет решал это `setTimeout(measureNav, 120)` — угадыванием момента; ResizeObserver
    // не угадывает.
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    for (const tab of container.children) observer.observe(tab)

    window.addEventListener('resize', measure)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [containerRef, activeIndex])

  return position
}
